import React, { useCallback, useEffect, useRef, useState } from 'react';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import colors from '../../../../../scss/colors';
import { useSelector } from 'react-redux';
import { useWindowSize } from '../../../../../utils/hooks';
import { responsiveSizeTimesheet } from '../../../../../utils/constants';

dayjs.extend(isSameOrBefore);

const HEAT_STOPS = ['#dceef9', '#9ac8ee', '#4d96d4', '#185fa5', colors.NAVY_BLUE];
const SECTION_COLORS = ['#1d9e75', '#185fa5', '#ba7517', '#d85a30', '#534ab7'];
const ARROW_PX = 80;
const THUMB_MIN_W = 36;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const formatHours = (number) => (number % 1 === 0 ? number : parseFloat(number.toFixed(2)));

function calculateCellStyle(total, maxTotal, isWeekend) {
    if (total === 0 && isWeekend) return { bg: colors.LIGHTER_BLUE, text: '#aaa', sub: '#bbb' };
    if (total === 0) return { bg: colors.LIGHTER_GREY, text: 'rgba(2,45,65,0.2)', sub: 'rgba(2,45,65,0.35)' };
    const intensity = clamp(total / maxTotal, 0, 1);
    const index = Math.round(intensity * (HEAT_STOPS.length - 1));
    return {
        bg: HEAT_STOPS[index],
        text: intensity < 0.5 ? colors.NAVY_BLUE : '#ffffff',
        sub: intensity < 0.5 ? 'rgba(2,45,65,0.55)' : 'rgba(255,255,255,0.65)',
    };
}

function getWorkTypeCategory(name = '') {
    const normalized = name.toLowerCase();
    if (normalized.includes('straight') || normalized.includes('regular')) return 'st';
    if (normalized.includes('over')) return 'ot';
    if (normalized.includes('pto') || normalized.includes('leave')) return 'pto';
    return 'other';
}

function aggregateDailyData(sections) {
    const map = {};

    sections?.forEach((section) => {
        section.TimesheetDetails?.forEach((detail) => {
            const category = getWorkTypeCategory(detail.WorkType?.Name);
            detail.TimesheetHoursByDates?.forEach(({ Day, TotalHrs }) => {
                const key = dayjs(Day).format('YYYY-MM-DD');
                if (!map[key]) {
                    map[key] = { date: dayjs(Day), total: 0, st: 0, ot: 0, pto: 0, other: 0, perDiem: false, sections: {} };
                }
                const hours = formatHours(Number(TotalHrs) || 0);
                map[key].total += hours;
                map[key][category] += hours;
                map[key].sections[section.Id] = formatHours((map[key].sections[section.Id] || 0) + hours);
            });
        });

        section.TimesheetPerDiem?.forEach(({ Date: d, IsPerDiem }) => {
            const key = dayjs(d).format('YYYY-MM-DD');
            if (!map[key]) {
                map[key] = { date: dayjs(d), total: 0, st: 0, ot: 0, pto: 0, other: 0, perDiem: false, sections: {} };
            }
            if (IsPerDiem) map[key].perDiem = true;
        });
    });

    return Object.entries(map)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([, d]) => ({
            ...d,
            total: formatHours(d.total),
            st: formatHours(d.st),
            ot: formatHours(d.ot),
            pto: formatHours(d.pto),
            other: formatHours(d.other),
            isWeekend: d.date.day() === 0 || d.date.day() === 6
        }));
}

const WorkTypeRow = ({ color, label, value, barPct, compact = false }) => (
    <div className="flex flex-col gap-1">
        <div className="flex items-center gap-2">
            <span className="w-2 h-2 rounded-[2px] flex-shrink-0" style={{ background: color }} />
            <span className={`${compact ? 'text-[11px]' : 'text-xs'} text-gray-500 flex-1`}>{label}</span>
            {compact && (
                <div className="flex-shrink-0 w-16 h-[3px] bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${barPct}%`, background: color }} />
                </div>
            )}
            <span className={`${compact ? 'text-[11px]' : 'text-xs'} font-medium`} style={{ color }}>{formatHours(value)} d</span>
        </div>
        {!compact && (
            <div className="h-[3px] bg-gray-100 rounded-full overflow-hidden">
                <div className="h-full rounded-full transition-all duration-500" style={{ width: `${barPct}%`, background: color }} />
            </div>
        )}
    </div>
);

function DailyHeatMap({ days, maxTotal, scrollRef, trackRef, thumbRef, thumbGeo, hasOverflow, hoveredDay, setHoveredDay, sectionMeta, onTrackClick, onThumbPointerDown, onThumbPointerMove, onThumbPointerUp, cellWidth = 66 }) {
    return (
        <>
            <div
                ref={scrollRef}
                className="flex overflow-x-auto flex-1"
                style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
                <div className="flex">
                    {days.map((d, i) => {
                        const total = d.total;
                        const { bg, text, sub } = calculateCellStyle(total, maxTotal, d.isWeekend);
                        const stPct = total > 0 ? (d.st / total) * 100 : 0;
                        const otPct = total > 0 ? (d.ot / total) * 100 : 0;
                        const ptoPct = total > 0 ? (d.pto / total) * 100 : 0;
                        const isLight = !bg || HEAT_STOPS.indexOf(bg) < 2;

                        return (
                            <div
                                key={i}
                                className="flex-shrink-0 flex flex-col items-center justify-center px-2 py-4
                                           cursor-default transition-[filter] duration-100 hover:brightness-90
                                           border-r border-white/10"
                                style={{
                                    width: cellWidth,
                                    background: bg ?? 'transparent',
                                    borderTop: d.isWeekend ? '2px solid rgba(0,0,0,0.06)' : undefined,
                                }}
                                onMouseEnter={() => setHoveredDay && setHoveredDay({ ...d, sectionMeta })}
                                onMouseLeave={() => setHoveredDay && setHoveredDay(null)}
                                onTouchStart={() => setHoveredDay && setHoveredDay({ ...d, sectionMeta })}
                            >
                                <span className="text-[10px] font-medium tracking-wide" style={{ color: sub }}>
                                    {d.date.format('ddd').toUpperCase()}
                                </span>
                                <span className="text-[10px] mb-2" style={{ color: sub }}>
                                    {d.date.format('D')}
                                </span>
                                <span className="text-xl font-medium leading-none" style={{ color: text }}>
                                    {formatHours(total) || '–'}
                                </span>

                                {d.perDiem && (
                                    <div
                                        className="mt-1 text-[8px] font-medium px-1 rounded"
                                        style={{
                                            background: isLight ? 'rgba(2,45,65,0.08)' : 'rgba(255,255,255,0.18)',
                                            color: isLight ? colors.NAVY_BLUE : 'rgba(255,255,255,0.85)',
                                        }}
                                    >
                                        PD
                                    </div>
                                )}

                                {total > 0 ? (
                                    <div className="flex gap-px overflow-hidden rounded-sm mt-1.5" style={{ width: 34, height: 3 }}>
                                        {d.st > 0 && <div style={{ width: `${stPct}%`, height: '100%', borderRadius: 1, background: isLight ? 'rgba(2,45,65,0.4)' : 'rgba(255,255,255,0.7)' }} />}
                                        {d.ot > 0 && <div style={{ width: `${otPct}%`, height: '100%', borderRadius: 1, background: '#f7c775' }} />}
                                        {d.pto > 0 && <div style={{ width: `${ptoPct}%`, height: '100%', borderRadius: 1, background: '#f0997b' }} />}
                                    </div>
                                ) : (
                                    <div style={{ height: d.perDiem ? 0 : 9 }} />
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>

            {hasOverflow && (
                <div className="flex items-center px-3 pt-1.5 pb-2 flex-shrink-0">
                    <div
                        ref={trackRef}
                        className="relative flex-1 h-[6px] bg-[#e1e6ea] rounded-full cursor-pointer overflow-hidden"
                        onClick={onTrackClick}
                    >
                        <div
                            ref={thumbRef}
                            className="absolute top-0 h-full rounded-full cursor-grab active:cursor-grabbing"
                            style={{ width: thumbGeo.width, transform: `translateX(${thumbGeo.left}px)`, background: colors.NAVY_BLUE }}
                            onPointerDown={onThumbPointerDown}
                            onPointerMove={onThumbPointerMove}
                            onPointerUp={onThumbPointerUp}
                            onPointerCancel={onThumbPointerUp}
                        />
                    </div>
                </div>
            )}
        </>
    );
}

export default function DailyTotalHeatStrip({ data, submissionType, workTypes, rateType }) {
    const size = useWindowSize();
    const isMobile = size.width <= responsiveSizeTimesheet;
    const approvers = useSelector((state) => state.user.approvers);

    const scrollRef = useRef(null);
    const trackRef = useRef(null);
    const thumbRef = useRef(null);
    const isDragging = useRef(false);
    const dragOrigin = useRef({ x: 0, left: 0 });

    const [hoveredDay, setHoveredDay] = useState(null);
    const [thumbGeo, setThumbGeo] = useState({ width: THUMB_MIN_W, left: 0 });
    const [hasOverflow, setHasOverflow] = useState(false);

    const days = aggregateDailyData(data);
    const maxTotal = Math.max(...days.map(d => d.total), 1);
    const grandST = formatHours(days.reduce((s, d) => s + d.st, 0));
    const grandOT = formatHours(days.reduce((s, d) => s + d.ot, 0));
    const grandPTO = formatHours(days.reduce((s, d) => s + d.pto, 0));
    const grandOther = formatHours(days.reduce((s, d) => s + d.other, 0));
    const grandTotal = formatHours(grandST + grandOT + grandPTO + grandOther);
    const maxWT = Math.max(grandST, grandOT, grandPTO, grandOther, 1);
    const grandPerDiem = days.filter(d => d.perDiem).length;

    const sectionMeta = Object.fromEntries(
        (data || []).map((s, i) => [
            s.Id,
            {
                name: approvers?.find(item => item.value === s.ApproverId)?.label || `Section ${i + 1}`,
                color: SECTION_COLORS[i % SECTION_COLORS.length],
            },
        ])
    );

    const updateScrollbarThumb = useCallback(() => {
        const el = scrollRef.current;
        const track = trackRef.current;
        if (el) setHasOverflow(el.scrollWidth > el.clientWidth);
        if (!el || !track) return;
        const trackWidth = track.clientWidth;
        const ratio = el.clientWidth / el.scrollWidth;
        const thumbWidth = Math.max(THUMB_MIN_W, ratio * trackWidth);
        const maxLeft = trackWidth - thumbWidth;
        const maxScroll = el.scrollWidth - el.clientWidth;
        setThumbGeo({ width: thumbWidth, left: maxScroll > 0 ? (el.scrollLeft / maxScroll) * maxLeft : 0 });
    }, []);

    useEffect(() => { updateScrollbarThumb(); }, [hasOverflow, updateScrollbarThumb]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        el.addEventListener('scroll', updateScrollbarThumb, { passive: true });
        return () => el.removeEventListener('scroll', updateScrollbarThumb);
    }, [updateScrollbarThumb]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        const ro = new ResizeObserver(updateScrollbarThumb);
        ro.observe(el);
        updateScrollbarThumb();
        return () => ro.disconnect();
    }, [updateScrollbarThumb]);

    useEffect(() => {
        const el = scrollRef.current;
        if (!el) return;
        let velocity = 0, rafId = null;
        const glide = () => {
            if (Math.abs(velocity) < 0.5) { velocity = 0; return; }
            el.scrollLeft += velocity;
            velocity *= 0.85;
            rafId = requestAnimationFrame(glide);
        };
        const handleWheel = (e) => {
            if (el.scrollWidth - el.clientWidth <= 0) return;
            e.preventDefault();
            velocity += (e.deltaY !== 0 ? e.deltaY : e.deltaX) * 0.4;
            if (rafId) cancelAnimationFrame(rafId);
            rafId = requestAnimationFrame(glide);
        };
        el.addEventListener('wheel', handleWheel, { passive: false });
        return () => { el.removeEventListener('wheel', handleWheel); if (rafId) cancelAnimationFrame(rafId); };
    }, []);

    useEffect(() => {
        const handleKeyDown = (e) => {
            const el = scrollRef.current;
            if (!el) return;
            if (e.key === 'ArrowRight') el.scrollLeft += ARROW_PX;
            if (e.key === 'ArrowLeft') el.scrollLeft -= ARROW_PX;
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, []);

    const syncScrollPosition = useCallback((left) => {
        const el = scrollRef.current;
        const track = trackRef.current;
        const thumb = thumbRef.current;
        if (!el || !track || !thumb) return;
        const maxLeft = track.clientWidth - thumb.offsetWidth;
        el.scrollLeft = (clamp(left, 0, maxLeft) / maxLeft) * (el.scrollWidth - el.clientWidth);
    }, []);

    const handleThumbPointerDown = (e) => {
        e.preventDefault();
        isDragging.current = true;
        dragOrigin.current = { x: e.clientX, left: thumbGeo.left };
        e.currentTarget.setPointerCapture(e.pointerId);
    };
    const handleThumbPointerMove = (e) => {
        if (!isDragging.current) return;
        syncScrollPosition(dragOrigin.current.left + e.clientX - dragOrigin.current.x);
    };
    const handleThumbPointerUp = () => { isDragging.current = false; };

    const handleTrackClick = (e) => {
        if (e.target !== trackRef.current) return;
        const rect = trackRef.current.getBoundingClientRect();
        const thumbW = thumbRef.current?.offsetWidth || thumbGeo.width;
        syncScrollPosition(e.clientX - rect.left - thumbW / 2);
    };

    const sharedStripProps = {
        days, maxTotal, scrollRef, trackRef, thumbRef, thumbGeo,
        hasOverflow, hoveredDay, setHoveredDay, sectionMeta,
        onTrackClick: handleTrackClick,
        onThumbPointerDown: handleThumbPointerDown,
        onThumbPointerMove: handleThumbPointerMove,
        onThumbPointerUp: handleThumbPointerUp,
    };

    const WorkTypeRows = ({ compact = false }) => (
        <>
            <WorkTypeRow compact={compact} color={colors.PRIMARY_BLUE} label="Straight Time" value={grandST} barPct={(grandST / maxWT) * 100} />
            <WorkTypeRow compact={compact} color="#ba7517" label="Overtime" value={grandOT} barPct={(grandOT / maxWT) * 100} />
            <WorkTypeRow compact={compact} color="#d85a30" label="PTO" value={grandPTO} barPct={(grandPTO / maxWT) * 100} />
            {grandOther > 0 && (
                <WorkTypeRow compact={compact} color="#534ab7" label="Other" value={grandOther} barPct={(grandOther / maxWT) * 100} />
            )}
        </>
    );

    const SectionLegend = ({ horizontal = false }) => (
        <div className={`flex ${horizontal ? 'flex-row flex-wrap gap-x-4 gap-y-1' : 'flex-col gap-1.5'}`}>
            {(data || []).map((section, i) => (
                <div key={section.Id} className="flex items-center gap-1.5 text-[11px] text-gray-500">
                    <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: SECTION_COLORS[i % SECTION_COLORS.length] }} />
                    <span className="truncate">
                        {approvers?.find(item => item.value === section.ApproverId)?.label || `Section ${i + 1}`}
                    </span>
                </div>
            ))}
        </div>
    );

    if (isMobile) {
        return (
            <section className="flex flex-col w-full rounded-xl shadow-md bg-white px-4 py-5 gap-3 min-h-170 md:min-h-full">
                <p className="text-sm font-medium tracking-[0.06em] text-navy-blue uppercase">
                    Daily totals
                </p>

                <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">

                    <div className="grid grid-cols-2 gap-2 p-3">
                        <div className="bg-gray-50 rounded-xl p-3 flex flex-col gap-0.5">
                            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-[0.06em]">Total</span>
                            <span className="text-2xl font-medium text-navy-blue leading-tight">{formatHours(grandTotal)}</span>
                            <span className="text-[11px] text-gray-400">days worked</span>
                        </div>
                        <div className="bg-gray-50 rounded-xl p-3 flex flex-col gap-0.5">
                            <span className="text-[10px] font-medium text-gray-400 uppercase tracking-[0.06em]">Per Diem</span>
                            <span className="text-2xl font-medium text-navy-blue leading-tight">{grandPerDiem}</span>
                            <span className="text-[11px] text-gray-400">qualifying days</span>
                        </div>
                    </div>

                    <div className="border-t border-gray-100">
                        <DailyHeatMap {...sharedStripProps} cellWidth={60} />
                    </div>

                    <div className="flex items-center flex-wrap gap-x-2 gap-y-1 px-3 py-2 bg-gray-50 border-t border-gray-100 min-h-[34px] text-[11px] flex-shrink-0">
                        {hoveredDay ? (
                            <>
                                <span className="font-medium text-navy-blue">{hoveredDay.date.format('ddd D')}</span>
                                <span className="text-gray-300">·</span>
                                {hoveredDay.total === 0 && !hoveredDay.perDiem ? (
                                    <span className="text-gray-400">No hours</span>
                                ) : (
                                    <>
                                        {hoveredDay.st > 0 && <span className="text-gray-600">ST: <strong>{formatHours(hoveredDay.st)}</strong>d</span>}
                                        {hoveredDay.ot > 0 && <span className="text-[#ba7517]">OT: <strong>{formatHours(hoveredDay.ot)}</strong>d</span>}
                                        {hoveredDay.pto > 0 && <span className="text-[#d85a30]">PTO: <strong>{formatHours(hoveredDay.pto)}</strong>d</span>}
                                        {hoveredDay.perDiem && <span className="text-[#185fa5] font-medium">Per Diem</span>}
                                    </>
                                )}
                            </>
                        ) : (
                            <span className="text-gray-400">Tap a day to see detail</span>
                        )}
                    </div>

                    <div className="px-4 py-3 border-t border-gray-100 flex flex-col gap-2">
                        <p className="text-[10px] font-medium text-gray-400 uppercase tracking-[0.06em] mb-1">By work type</p>
                        <WorkTypeRows compact />
                    </div>

                    <div className="px-4 pb-3 border-t border-gray-100 pt-3">
                        <p className="text-[10px] font-medium text-gray-400 uppercase tracking-[0.06em] mb-2">Sections</p>
                        <SectionLegend horizontal />
                    </div>
                </div>
            </section>
        );
    }

    return (
        <section className="flex flex-col w-full rounded-xl shadow-md bg-white px-5 py-6 gap-3">
            <p className="text-base font-medium tracking-[0.07em] text-navy-blue uppercase">
                Daily totals — all sections
            </p>

            <div className="flex bg-white border border-gray-200 rounded-xl overflow-hidden">

                <div className="w-48 flex-shrink-0 p-4 border-r border-gray-100 flex flex-col gap-3">
                    <p className="text-xs font-medium tracking-[0.07em] text-gray-400 uppercase">By work type</p>
                    <WorkTypeRows />
                    <div className="mt-auto pt-3 border-t border-gray-100 flex flex-col gap-1.5">
                        <p className="text-xs font-medium tracking-[0.07em] text-gray-400 uppercase">Sections</p>
                        <SectionLegend />
                    </div>
                </div>

                <div className="flex flex-col flex-1 min-w-0 overflow-hidden">
                    <DailyHeatMap {...sharedStripProps} cellWidth={66} />

                    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border-t border-gray-100 min-h-[34px] text-xs flex-shrink-0">
                        {hoveredDay ? (
                            <>
                                <span className="font-medium text-navy-blue">{hoveredDay.date.format('ddd D')}</span>
                                <span className="text-gray-300">·</span>
                                {hoveredDay.total === 0 && !hoveredDay.perDiem ? (
                                    <span className="text-gray-400">No hours recorded</span>
                                ) : (
                                    <>
                                        {hoveredDay.st > 0 && <span className="text-gray-600">ST: <strong>{formatHours(hoveredDay.st)}</strong>d</span>}
                                        {hoveredDay.ot > 0 && <span className="text-[#ba7517]">OT: <strong>{formatHours(hoveredDay.ot)}</strong>d</span>}
                                        {hoveredDay.pto > 0 && <span className="text-[#d85a30]">PTO: <strong>{formatHours(hoveredDay.pto)}</strong>d</span>}
                                        {hoveredDay.perDiem && <span className="text-[#185fa5] font-medium">· Per Diem</span>}
                                    </>
                                )}
                                <div className="ml-auto flex gap-3">
                                    {Object.entries(hoveredDay.sections)
                                        .filter(([, v]) => v > 0)
                                        .map(([sId, hrs]) => (
                                            <span key={sId} className="flex items-center gap-1 text-gray-500">
                                                <span className="w-1.5 h-1.5 rounded-full" style={{ background: hoveredDay.sectionMeta[sId]?.color }} />
                                                {hoveredDay.sectionMeta[sId]?.name}: {formatHours(hrs)}d
                                            </span>
                                        ))}
                                </div>
                            </>
                        ) : (
                            <span className="text-gray-400">Hover a day to see breakdown</span>
                        )}
                    </div>
                </div>

                <div className="w-[88px] flex-shrink-0 border-l border-gray-100 flex flex-col items-center justify-center gap-0.5 px-3 py-4">
                    <p className="text-sm font-medium tracking-[0.07em] text-gray-400 uppercase text-center">Total</p>
                    <p className="text-[26px] font-medium text-navy-blue leading-tight">{formatHours(grandTotal)}</p>
                    <p className="text-xs text-gray-400">days</p>
                    <div className="w-8 h-px bg-gray-200 my-2" />
                    <p className="text-[10px] font-medium tracking-[0.07em] text-gray-400 uppercase text-center">Per Diem</p>
                    <p className="text-lg font-medium text-navy-blue">{grandPerDiem}</p>
                    <p className="text-xs text-gray-400">days</p>
                </div>

            </div>
        </section>
    );
}